import { useForm, Head } from '@inertiajs/react'
import Logo from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { FormEvent, ReactNode } from 'react'

type Props = {
  user: {
    first_name: string
    last_name: string
    email: string
    username: string
  }
}

export default function CompleteProfile({ user }: Props) {
  const { data, setData, patch, processing, errors, transform } = useForm({
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    transform(formData => ({ user: formData }))
    patch('/users/complete_profile')
  }

  return (
    <>
      <Head title="Complete Your Profile" />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-accent/10 to-primary/5 px-4">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center">
            <Logo />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-semibold">Complete your profile</CardTitle>
              <CardDescription>
                Just a few details before you get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First name</Label>
                    <Input
                      id="first_name"
                      value={data.first_name}
                      onChange={e => setData('first_name', e.target.value)}
                      autoFocus
                    />
                    {errors.first_name && (
                      <p className="text-base text-destructive">{errors.first_name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last name</Label>
                    <Input
                      id="last_name"
                      value={data.last_name}
                      onChange={e => setData('last_name', e.target.value)}
                    />
                    {errors.last_name && (
                      <p className="text-base text-destructive">{errors.last_name}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={data.username}
                    onChange={e => setData('username', e.target.value)}
                  />
                  {errors.username && (
                    <p className="text-base text-destructive">{errors.username}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={processing}>
                  Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

CompleteProfile.layout = (page: ReactNode) => page
